import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';
import { sortFindings } from '../../../utils/reportSharedUtils';
import { CVSS31 } from '../../../../../utils/cvss31/cvsscalc31';
import { CVSS40 } from '@pandatix/js-cvss';

interface FindingsSummaryPageProps {
  reportData: ReportData;
  sectionId?: string;
  pieChartImage?: string;
  barChartImage?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 16,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 36,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 4,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  idPill: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    alignSelf: 'center',
  },
  idPillText: {
    color: '#475569',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  severityPill: {
    borderRadius: 10,
    paddingVertical: 3.5,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 54,
    flexDirection: 'row',
  },
  severityPillText: {
    color: '#FFFFFF',
    fontSize: 7.5,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  noFindingsBox: {
    padding: 20,
    textAlign: 'center',
    color: '#16A34A',
    fontWeight: 'bold',
    borderWidth: 1,
    borderColor: '#D1FAE5',
    backgroundColor: '#ECFDF5',
    borderRadius: 6,
    marginTop: 12,
  },
});

const FindingsSummaryPageV2: React.FC<FindingsSummaryPageProps> = ({ reportData, sectionId }) => {
  const rawFindings = reportData.detailed_findings || [];
  const findings = sortFindings(rawFindings);

  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const sevColors: Record<string, string> = {
    'Critical': '#E11D48',
    'High': '#F97316',
    'Medium': '#EAB308',
    'Low': '#16A34A',
    'Info': '#8B9BB4'
  };

  const getFriendlyStatus = (status: string) => {
    if (!status) return 'Open';
    const statusLower = status.toLowerCase().trim();
    if (statusLower === 'businessacceptsrisk' || statusLower === 'business accepts risk' || statusLower === 'accepts risk') {
      return 'Risk Accepted';
    }
    if (statusLower === 'businessnonactionable' || statusLower === 'business non actionable' || statusLower === 'non-actionable') {
      return 'Non-Actionable';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getAffectedLabel = (scopeVal: string) => {
    if (!scopeVal) return '\u2014';
    const trimmed = scopeVal.trim();
    const lower = trimmed.toLowerCase();
    
    if (lower.includes('host') || lower.includes('account') || lower.includes('domain') || lower.includes('wide')) {
      return trimmed;
    }
    
    let ipPart = trimmed;
    if (trimmed.includes('|')) {
      const parts = trimmed.split('|');
      ipPart = parts[1] || parts[0];
    }
    
    const items = ipPart.split(',').map(item => item.trim()).filter(Boolean);
    if (items.length > 0) {
      const isAccount = lower.includes('user') || lower.includes('account') || lower.includes('member');
      const unit = isAccount ? 'account' : 'host';
      return `${items.length} ${unit}${items.length > 1 ? 's' : ''}`;
    }
    
    return trimmed;
  };

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Title */}
      <Text style={styles.sectionTitle}>6. Findings Summary</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      {findings.length === 0 ? (
        <View style={styles.noFindingsBox}>
          <Text>No findings identified during this assessment.</Text>
        </View>
      ) : (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            {isNetwork ? (
              <>
                <Text style={[styles.tableHeaderCell, { width: '10%', textAlign: 'center' }]}>ID</Text>
                <Text style={[styles.tableHeaderCell, { width: '32%' }]}>Finding</Text>
                <Text style={[styles.tableHeaderCell, { width: '15%' }]}>Affected</Text>
                <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>Severity</Text>
                <Text style={[styles.tableHeaderCell, { width: '10%', textAlign: 'center' }]}>CVSS</Text>
                <Text style={[styles.tableHeaderCell, { width: '18%' }]}>Status</Text>
              </>
            ) : (
              <>
                <Text style={[styles.tableHeaderCell, { width: '8%', textAlign: 'center' }]}>ID</Text>
                <Text style={[styles.tableHeaderCell, { width: '46%' }]}>Finding</Text>
                <Text style={[styles.tableHeaderCell, { width: '14%', textAlign: 'center' }]}>Severity</Text>
                <Text style={[styles.tableHeaderCell, { width: '7%', textAlign: 'center' }]}>CVSS</Text>
                <Text style={[styles.tableHeaderCell, { width: '25%' }]}>Status</Text>
              </>
            )}
          </View>
          {findings.map((f, idx) => {
            const sev = f.severity_category || 'Info';
            const pillColor = sevColors[sev] || '#64748B';
            const statusText = getFriendlyStatus(f.status || 'Open');
            const isLast = idx === findings.length - 1;

            const cvssScore = (() => {
              if (f.cvss_score != null) {
                return String(f.cvss_score);
              }
              const vec = f.cvss_vector || f.severity || '';
              if (vec && typeof vec === 'string' && vec.includes('CVSS')) {
                try {
                  if (f.cvss_version === '4.0' || vec.includes('CVSS:4.0')) {
                    const calc40 = new CVSS40(vec);
                    return calc40.Score().toFixed(1);
                  }
                  const calc = (CVSS31 as any).calculateCVSSFromVector(vec);
                  if (calc && calc.baseMetricScore) return String(calc.baseMetricScore);
                } catch (_) {}
              }
              return 'N/A';
            })();

            return (
              <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
                {isNetwork ? (
                  <>
                    {/* ID Pill */}
                    <View style={[styles.tableRowCell, { width: '10%', alignItems: 'center', justifyContent: 'center' }]}>
                      <View style={styles.idPill}>
                        <Text style={styles.idPillText}>
                          {f.report_id || f.id || `F-${idx + 1}`}
                        </Text>
                      </View>
                    </View>

                    {/* Title */}
                    <Text style={[styles.tableRowCell, { width: '32%', color: '#1F2937' }]}>
                      {f.title}
                    </Text>

                    {/* Affected */}
                    <Text style={[styles.tableRowCell, { width: '15%', color: '#374151' }]}>
                      {getAffectedLabel(f.scope)}
                    </Text>

                    {/* Severity Pill */}
                    <View style={[styles.tableRowCell, { width: '15%', alignItems: 'center', justifyContent: 'center' }]}>
                      <View style={[styles.severityPill, { backgroundColor: pillColor }]}>
                        <Text style={styles.severityPillText}>{sev}</Text>
                      </View>
                    </View>

                    {/* CVSS Score */}
                    <Text style={[styles.tableRowCell, { width: '10%', textAlign: 'center', color: '#1F2937' }]}>
                      {cvssScore}
                    </Text>

                    {/* Status */}
                    <Text style={[styles.tableRowCell, { width: '18%', color: '#374151' }]}>
                      {statusText}
                    </Text>
                  </>
                ) : (
                  <>
                    {/* ID Pill */}
                    <View style={[styles.tableRowCell, { width: '8%', alignItems: 'center', justifyContent: 'center' }]}>
                      <View style={styles.idPill}>
                        <Text style={styles.idPillText}>
                          {f.report_id || f.id || `F-${idx + 1}`}
                        </Text>
                      </View>
                    </View>

                    {/* Title */}
                    <Text style={[styles.tableRowCell, { width: '46%', color: '#1F2937' }]}>
                      {f.title}
                    </Text>

                    {/* Severity Pill */}
                    <View style={[styles.tableRowCell, { width: '14%', alignItems: 'center', justifyContent: 'center' }]}>
                      <View style={[styles.severityPill, { backgroundColor: pillColor }]}>
                        <Text style={styles.severityPillText}>{sev}</Text>
                      </View>
                    </View>

                    {/* CVSS Score */}
                    <Text style={[styles.tableRowCell, { width: '7%', textAlign: 'center', color: '#1F2937' }]}>
                      {cvssScore}
                    </Text>

                    {/* Status */}
                    <Text style={[styles.tableRowCell, { width: '25%', color: '#374151' }]}>
                      {statusText}
                    </Text>
                  </>
                )}
              </View>
            );
          })}
        </View>
      )}
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default FindingsSummaryPageV2;
